import React, { useState, useEffect, lazy, Suspense, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import gsap from 'gsap';
import ConfirmLogoutModal from '../components/ConfirmLogoutModal';

// Lazy Load Admin Pages
const AdminDashboard = lazy(() => import('../pages/admin/AdminDashboard'));
const CreateTournament = lazy(() => import('../pages/admin/CreateTournament'));
const ManageTournaments = lazy(() => import('../pages/admin/ManageTournaments'));
const VerifyPayments = lazy(() => import('../pages/admin/VerifyPayments'));
const AdminUserDashboard = lazy(() => import('../pages/admin/AdminUserDashboard'));
const AdminNotifications = lazy(() => import('../pages/admin/AdminNotifications'));
const RoomIdDistribution = lazy(() => import('../pages/admin/RoomIdDistribution'));
const UserManagement = lazy(() => import('../pages/admin/UserManagement'));
const AppUpdateManagement = lazy(() => import('../pages/admin/AppUpdateManagement'));
const AllTransactions = lazy(() => import('../pages/admin/AllTransactions'));
const ErrorPage = lazy(() => import('../pages/ErrorPage'));
const AdminLogin = lazy(() => import('../pages/auth/AdminLogin'));
const AdminEmailDashboard = lazy(() => import('../pages/admin/AdminEmailDashboard'));
const AdminProfits = lazy(() => import('../pages/admin/AdminProfits'));
const FFVerifications = lazy(() => import('../pages/admin/FFVerifications'));
const AppSettings = lazy(() => import('../pages/admin/AppSettings'));

// Admin Loading UI
const AdminPageLoader = () => (
    <div className="flex flex-col items-center justify-center p-12 w-full">
        <div className="w-10 h-10 border-4 border-red-500/20 border-t-red-500 rounded-full animate-spin mb-4"></div>
        <p className="text-gray-400 text-sm">Loading secure module...</p>
    </div>
);


const AdminLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showError, setShowError] = useState(false);
    const [currentView, setCurrentView] = useState('landing');
    const [errorState, setErrorState] = useState({ code: 404, message: '' });
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const contentRef = useRef(null);

    // Page Transition Animation - Cinematic Flow
    useEffect(() => {
        if (contentRef.current) {
            gsap.fromTo(contentRef.current,
                { opacity: 0, y: 40, scale: 0.98 },
                { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: "expo.out" }
            );
        }
    }, [location.pathname]);

    // Check authentication on component mount and route changes
    useEffect(() => {
        checkAuthentication();
    }, [location.pathname]);

    // Replace localStorage checks with Cookies
    const checkAuthentication = async () => {
        try {
            // Validate session with an admin profile/check call
            await axios.get(`${backendUrl}/admin/profile`);
            setIsAuthenticated(true);
        } catch (error) {
            if (error.response?.status === 401 && error.response?.data?.reason === 'session_expired') {
                // Modal is shown by the interceptor, we stay in loading
                return;
            }
            setIsAuthenticated(false);
            if (!location.pathname.includes('/login')) {
                navigate('/admin/login');
            }
        } finally {
            setLoading(false);
        }
    }

    // Update logout function
    const handleLogoutClick = () => {
        setIsSidebarOpen(false);
        setIsLogoutModalOpen(true);
    };

    const handleLogoutConfirm = async () => {
        setIsLoggingOut(true);
        try {
            // Call backend logout
            await axios.post(`${backendUrl}/api/logout`);
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setIsAuthenticated(false);
            setIsLoggingOut(false);
            setIsLogoutModalOpen(false);
            navigate('/admin/login');
        }
    };

    // Admin sidebar items
    const adminMenuItems = [
        // --- MOST USED: DAILY OPERATIONS ---
        { icon: 'ri-dashboard-line', label: 'Admin Dashboard', path: '/admin/dashboard' },
        { icon: 'ri-money-dollar-circle-line', label: 'Verify Payments', path: '/admin/verify-payments' },
        { icon: 'ri-key-line', label: 'Room ID Distribution', path: '/admin/room-distribution' },
        { icon: 'ri-shield-user-line', label: 'FF Verifications', path: '/admin/ff-verifications' },

        // --- CORE TOURNAMENT MGMT ---
        { icon: 'ri-trophy-line', label: 'Create Tournament', path: '/admin/create-tournament' },
        { icon: 'ri-settings-line', label: 'Manage Tournaments', path: '/admin/manage-tournaments' },

        // --- USER & INTEL ---
        { icon: 'ri-user-settings-line', label: 'User Management', path: '/admin/user-management' },
        { icon: 'ri-user-search-line', label: 'User Intelligence', path: '/admin/user-intelligence' },

        // --- FINANCE & AUDIT ---
        { icon: 'ri-money-rupee-circle-line', label: 'Your Profits', path: '/admin/profits' },
        { icon: 'ri-exchange-funds-line', label: 'All Transactions', path: '/admin/all-transactions' },

        // --- COMMS & SYSTEM ---
        { icon: 'ri-notification-line', label: 'Admin Notifications', path: '/admin/notifications' },
        { icon: 'ri-rocket-line', label: 'App Update Notice', path: '/admin/app-updates' },
        { icon: 'ri-settings-4-line', label: 'App Configuration', path: '/admin/settings' },
        { icon: 'ri-mail-send-line', label: 'Email Center', path: '/admin/email-center' },
    ];

    const handleNavigation = (path) => {
        navigate(path);
        setIsSidebarOpen(false);
        setShowError(false);
    };

    const isActivePath = (path) => {
        return location.pathname === path;
    };

    const handleGoDashboard = () => {
        navigate('/admin/dashboard');
        setShowError(false);
    };

    // Check if current route is valid
    const isValidRoute = () => {
        const validRoutes = [
            '/admin/dashboard',
            '/admin/create-tournament',
            '/admin/manage-tournaments',
            '/admin/verify-payments',
            '/admin/user-intelligence',
            '/admin/notifications',
            '/admin/room-distribution',
            '/admin/user-management',
            '/admin/all-transactions',
            '/admin/login',
            '/admin/app-updates',
            '/admin/email-center',
            '/admin/profits',
            '/admin/ff-verifications',
            '/admin/settings'
        ];
        return validRoutes.includes(location.pathname);
    };

    // Function to handle API errors from any component
    const handleApiError = (error) => {
        const statusCode = error.response?.status || 500;
        const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';

        setErrorState({
            code: statusCode,
            message: errorMessage
        });
        setCurrentView('error');
    };

    // Show loading while checking authentication
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-400">Checking authentication...</p>
                </div>
            </div>
        );
    }

    // If on login route, show only login page
    if (location.pathname === '/admin/login') {
        return <AdminLogin />;
    }

    // If invalid route, show only error page
    if (!isValidRoute()) {
        return (
            <div className="min-h-screen bg-gray-900">
                <ErrorPage
                    errorCode={404}
                    errorMessage="The admin page you're looking for doesn't exist."
                    onGoHome={handleGoDashboard}
                    showLanding={false}
                />
            </div>
        );
    }

    // If not authenticated, redirect will happen automatically
    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-400">Redirecting to login...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white flex">
            <ConfirmLogoutModal
                isOpen={isLogoutModalOpen}
                onClose={() => setIsLogoutModalOpen(false)}
                onConfirm={handleLogoutConfirm}
                isLoading={isLoggingOut}
            />
            {/* Mobile Header - FIXED */}
            <header className="bg-gray-800 border-b border-gray-700 px-6 py-4 lg:hidden fixed top-0 left-0 right-0 z-50 h-16">
                <div className="flex items-center justify-between h-full">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className="p-2 rounded-lg hover:bg-gray-700 transition-colors"
                        >
                            <i className="ri-menu-line text-xl"></i>
                        </button>

                        <div className="flex items-center space-x-2">
                            <i className="ri-admin-line text-2xl text-red-500"></i>
                            <span className="text-xl font-bold text-white">
                                FF ARENA ADMIN
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center space-x-4">
                        <button
                            onClick={handleLogoutClick}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center space-x-2"
                        >
                            <i className="ri-logout-box-r-line"></i>
                        </button>
                    </div>
                </div>
            </header>

            {/* Sidebar Overlay */}
            {isSidebarOpen && (
                <div
                    className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
                    onClick={() => setIsSidebarOpen(false)}
                ></div>
            )}

            {/* Sidebar - FIXED */}
            <aside className={`
                fixed top-16 left-0 h-[calc(100vh-64px)] w-64 bg-gray-800 border-r border-gray-700 z-40
                transform transition-transform duration-300 ease-in-out flex flex-col
                ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                {/* Sidebar Header */}
                <div className="p-6 border-b border-gray-700 flex-shrink-0">
                    <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-yellow-500 rounded-xl flex items-center justify-center">
                            <i className="ri-shield-keyhole-fill text-white text-xl"></i>
                        </div>
                        <div>
                            <h3 className="font-semibold">Admin Panel</h3>
                            <span className="text-green-400 text-sm">Online</span>
                        </div>
                    </div>
                </div>

                {/* Navigation Menu */}
                <nav className="p-4 pb-24 space-y-2 flex-1 overflow-y-auto">
                    {adminMenuItems.map((item) => (
                        <button
                            key={item.path}
                            onClick={() => handleNavigation(item.path)}
                            className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors group text-left ${isActivePath(item.path)
                                ? 'bg-gradient-to-r from-red-500 to-yellow-500 text-white shadow-lg'
                                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                }`}
                        >
                            <i className={`${item.icon} text-xl ${isActivePath(item.path) ? 'text-white' : 'group-hover:text-yellow-400'
                                }}`}></i>
                            <span className="font-medium">{item.label}</span>
                        </button>
                    ))}
                </nav>
            </aside>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col lg:ml-64 lg:mt-0 min-h-screen">
                {/* Desktop Header - FIXED */}
                <header className="bg-gray-800 border-b border-gray-700 px-6 py-4 hidden lg:block fixed top-0 right-0 left-0 z-30 h-16">
                    <div className="flex items-center justify-between h-full">
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <i className="ri-admin-line text-2xl text-red-500"></i>
                                <span className="text-xl font-bold text-white">
                                    FF ARENA ADMIN
                                </span>
                            </div>
                        </div>

                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-3">
                                <div className="text-right">
                                    <div className="font-semibold text-sm">Admin User</div>
                                    <div className="text-gray-400 text-xs">Super Admin</div>
                                </div>
                                <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-yellow-500 rounded-full flex items-center justify-center">
                                    <i className="ri-user-fill text-white"></i>
                                </div>
                            </div>

                            <button
                                onClick={handleLogoutClick}
                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center space-x-2"
                            >
                                <i className="ri-logout-box-r-line"></i>
                                <span>Logout</span>
                            </button>
                        </div>
                    </div>
                </header>

                {/* Main Content - SCROLLABLE */}
                <main className="flex-1 overflow-auto mt-20 relative">
                    <Suspense fallback={<AdminPageLoader />}>
                        <div
                            ref={contentRef}
                            className="h-full"
                        >
                            <Routes>
                                <Route path="/dashboard" element={<AdminDashboard />} />
                                <Route path="/create-tournament" element={<CreateTournament onApiError={handleApiError} />} />
                                <Route path="/manage-tournaments" element={<ManageTournaments />} />
                                <Route path="/verify-payments" element={<VerifyPayments />} />
                                <Route path="/ff-verifications" element={<FFVerifications />} />
                                <Route path="/profits" element={<AdminProfits />} />
                                <Route path="/user-intelligence" element={<AdminUserDashboard />} />
                                <Route path="/notifications" element={<AdminNotifications />} />
                                <Route path="/room-distribution" element={<RoomIdDistribution />} />
                                <Route path="/user-management" element={<UserManagement />} />
                                <Route path="/app-updates" element={<AppUpdateManagement />} />
                                <Route path="/all-transactions" element={<AllTransactions />} />
                                <Route path="/email-center" element={<AdminEmailDashboard />} />
                                <Route path="/settings" element={<AppSettings />} />
                            </Routes>
                        </div>
                    </Suspense>
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;